
import { useState, useEffect } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Mail, LogIn, Check, Clock, Users, Zap } from "lucide-react";
import { isUserRegistered, checkTenantExists } from "@/services/authService";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";


export default function SignupPage() {
  const { user, session, isLoading, authStatus, signInWithMicrosoft } = useAuth();
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [isProcessingMarketplace, setIsProcessingMarketplace] = useState(false);
  const [marketplaceError, setMarketplaceError] = useState<string | null>(null);
  const [marketplaceState, setMarketplaceState] = useState<{
    flow: string;
    marketplace_token: string;
  } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Handle Microsoft callback and redirect to complete
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isAuthCallback = params.has('code');
    
    // Check for marketplace_token in URL (passed via redirectTo)
    const marketplaceToken = params.get('marketplace_token');

    if (marketplaceToken) {
      console.log("SignupPage - Marketplace token detected in URL");
      
      // Store marketplace state for other effects to access
      setMarketplaceState({
        flow: 'marketplace',
        marketplace_token: marketplaceToken
      });
      
      setIsProcessingMarketplace(true);
    }
    
    if (isAuthCallback) {
      if (!user) {
        setIsSigningUp(true);
      }

      // Redirect to complete page once user data is available (only if not marketplace flow)
      if (location.pathname === '/signup/callback' && user && !marketplaceToken) {
        console.log("SignupPage - Callback with user, redirecting to complete");
        navigate('/signup/complete');
      }
    }
  }, [location, user, session, navigate]);
  
  // Check user status after authentication
  useEffect(() => {
    console.log("SignupPage - Auth state check - User:", !!user, "isChecking:", authStatus.isChecking);
    
    // Only run this effect when we have a user
    if (user) {
      console.log("SignupPage - Checking user registration status:", user.id);
      // If the user is authenticated, check if they're registered
      isUserRegistered(user.id).then(registered => {
        console.log("SignupPage - User registered:", registered);
        if (!registered) {
          // Check if tenant already has a master user
          const tenantId = user.app_metadata?.tenant_id;
          if (tenantId) {
            console.log("SignupPage - Checking if tenant exists:", tenantId);
            checkTenantExists(tenantId).then(tenantExists => {
              console.log("SignupPage - Tenant exists check result:", tenantExists);
              setIsSigningUp(false); // Clear signing up state
              
              if (tenantExists) {
                // If tenant already exists, redirect to error page
                navigate("/error");
              } else if (location.pathname !== '/signup/complete' && !marketplaceState) {
                // Only redirect if NOT in marketplace flow
                navigate("/signup/complete");
              }
              // If in marketplace flow, let the marketplace processing useEffect handle navigation
            });
          } else {
            // No tenant ID in metadata
            console.error("SignupPage - No tenant ID found in user metadata");
            toast.error("Authentication failed: No organization ID found");
            setIsSigningUp(false);
          }
        } else if (authStatus.isRegistered) {
          // If already registered and authorized, redirect to dashboard
          setIsSigningUp(false);
          navigate("/");
        }
      });
    }
  }, [user, authStatus, navigate, location.pathname]);

  // Detect token parameter and auto-trigger Microsoft login
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    
    // Only process if we have a token AND user is not authenticated
    if (token && !user && !isLoading) {
      console.log("SignupPage - Marketplace token detected, initiating OAuth with state");
      
      // Trigger Microsoft login with the token in state
      setIsSigningUp(true);
      signInWithMicrosoft(window.location.origin + "/signup/callback", { marketplace_token: token }).catch(error => {
        console.error("Failed to initiate Microsoft login:", error);
        setMarketplaceError("Failed to connect to Microsoft. Please try again.");
        setIsSigningUp(false);
      });
    }
  }, [location.search, user, isLoading, signInWithMicrosoft]);

  // Process marketplace subscription after authentication
  useEffect(() => {
    // Use the stored marketplace state
    if (!user || !marketplaceState) return;

    // Only process if we're in marketplace flow and haven't processed yet
    if (marketplaceState.flow === 'marketplace' && 
        marketplaceState.marketplace_token && 
        isProcessingMarketplace) {

      const token = marketplaceState.marketplace_token;
      console.log("SignupPage - Processing marketplace subscription with token");
      
      supabase.functions.invoke('ms-marketplace-resolve-subscription', {
        body: { token }
      })
      .then(({ data, error }) => {
        if (error) {
          console.error("SignupPage - Marketplace subscription error:", error);
          setMarketplaceError("Failed to activate marketplace subscription. Please contact support.");
          toast.error("Subscription activation failed");
        } else {
          console.log("SignupPage - Marketplace subscription resolved:", data);
          toast.success("Microsoft Marketplace subscription activated successfully!");
        }
      })
      .finally(() => {
        setIsProcessingMarketplace(false);
        setMarketplaceState(null); // Clear marketplace state
        
        // Clean up URL by removing the state parameter
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);
        
        // Navigate to complete page
        navigate('/signup/complete');
      });
    }
  }, [user, marketplaceState, isProcessingMarketplace, navigate]);

  const handleMicrosoftSignUp = async () => {
    try {
      setIsSigningUp(true);
      await signInWithMicrosoft(window.location.origin + "/signup/callback");
    } catch (error: any) {
      console.error("Signup error:", error);
      setIsSigningUp(false);
    }
  };

  // Show processing state for marketplace flow
  if (isProcessingMarketplace) {
    return (
      <div className="flex min-h-screen bg-background items-center justify-center">
        <div className="text-center max-w-md">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold mb-2">Activating Your Subscription</h2>
          <p className="text-muted-foreground">
            We're setting up your Microsoft Marketplace subscription. This will only take a moment...
          </p>
        </div>
      </div>
    );
  }

  // Show error state if marketplace processing failed
  if (marketplaceError) {
    return (
      <div className="flex min-h-screen bg-background items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold mb-2">Subscription Activation Failed</h2>
          <p className="text-muted-foreground mb-4">{marketplaceError}</p>
          <Button onClick={() => navigate('/signup')}>
            Return to Signup
          </Button>
        </div>
      </div>
    );
  }

  // If user is already logged in and registered, redirect to dashboard
  if (user && authStatus.isRegistered && !authStatus.isChecking) {
    return <Navigate to="/" />;
  }

  return (
    <div className="flex min-h-screen bg-background">
      {/* Left side - Hero content */}
      <div className="hidden lg:flex lg:w-1/2 bg-muted/20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary/10" />
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16">
          <div className="max-w-lg">
            <Badge variant="secondary" className="mb-6 w-fit">
              <Clock className="w-3 h-3 mr-1" />
              14-day free trial
            </Badge>
            
            <h1 className="text-4xl xl:text-5xl font-bold text-foreground mb-6 leading-tight">
              Automate your Microsoft 365 
              <span className="text-primary"> shared mailboxes</span>
            </h1>
            
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Transform your team's email management with intelligent auto-replies. 
              Perfect for support teams, sales inquiries, and shared inboxes.
            </p>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                  <Check className="w-3 h-3 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-foreground">Smart Auto-Replies</p>
                  <p className="text-sm text-muted-foreground">Intelligent responses based on email content and sender</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                  <Users className="w-3 h-3 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-foreground">Shared Mailbox Support</p>
                  <p className="text-sm text-muted-foreground">Works seamlessly with Microsoft 365 shared mailboxes</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                  <Zap className="w-3 h-3 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-foreground">Instant Setup</p>
                  <p className="text-sm text-muted-foreground">Connect your Microsoft account and start automating in minutes</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
      </div>

      {/* Right side - Sign up form */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8 lg:hidden">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary mx-auto mb-4">
              <Mail className="h-6 w-6 text-primary-foreground" />
            </div>
            <Badge variant="secondary" className="mb-4">
              <Clock className="w-3 h-3 mr-1" />
              14-day free trial
            </Badge>
          </div>
          
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-foreground mb-2">
              Get started with AutoReply365
            </h2>
            <p className="text-muted-foreground">
              Connect your Microsoft 365 account to automate your shared mailboxes
            </p>
          </div>

          <div className="space-y-6">
            <div className="rounded-lg bg-primary/5 border border-primary/10 p-6">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Mail className="w-4 h-4 text-primary" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-foreground mb-1">Microsoft 365 Integration</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    We'll securely connect to your Microsoft 365 account to access your shared mailboxes and enable automation.
                  </p>
                  <div className="text-xs text-muted-foreground">
                    ✓ Secure OAuth connection • ✓ Read-only mailbox access • ✓ Enterprise-grade security
                  </div>
                </div>
              </div>
            </div>
            
            <Button 
              type="button" 
              size="lg"
              className="w-full h-12 text-base font-medium" 
              onClick={handleMicrosoftSignUp}
              disabled={isLoading || isSigningUp}
            >
              <LogIn className="mr-3 h-5 w-5" />
              {isSigningUp || isLoading ? "Connecting to Microsoft..." : "Connect with Microsoft 365"}
            </Button>

            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                Already have an account?{" "}
                <a href="/login" className="font-medium text-primary hover:underline">
                  Sign in here
                </a>
              </p>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-border">
            <p className="text-xs text-muted-foreground text-center">
              By connecting your Microsoft account, you agree to our terms of service 
              and privacy policy. Your data is encrypted and secure.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
